import React, { useState } from 'react';
import { Button } from '../ui/button';
import {
  Globe,
  Loader2,
  Check,
  Languages,
  ChevronDown,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { aiAPI } from '../../services/api';

const LanguageTranslator = ({ campaignId, originalTitle, originalStory, onTranslate }) => {
  const [loading, setLoading] = useState(false);
  const [selectedLang, setSelectedLang] = useState('en'); // Default to English
  const [translation, setTranslation] = useState(null);
  const [error, setError] = useState(null);

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'ms', name: 'Bahasa Malaysia', flag: '🇲🇾' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'ta', name: 'தமிழ்', flag: '🇮🇳' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'ko', name: '한국어', flag: '🇰🇷' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  ];

  const handleTranslate = async (langCode) => {
    // Reset error
    setError(null);
    
    if (langCode === 'en') {
      setTranslation(null);
      setSelectedLang('en');
      if (onTranslate) onTranslate(null);
      return;
    }

    setLoading(true);
    setSelectedLang(langCode);
    
    try {
      console.log('Translating campaign:', campaignId, 'to', langCode);
      const result = await aiAPI.translateCampaign(campaignId, langCode);
      console.log('Translation result:', result);
      
      if (result && result.translated_title && result.translated_story) {
        setTranslation(result);
        if (onTranslate) onTranslate(result);
        setError(null);
      } else {
        throw new Error('Invalid translation response');
      }
    } catch (err) {
      console.error('Translation failed:', err);
      setError('Translation failed. Please try again.');
      setSelectedLang('en');
      setTranslation(null);
      if (onTranslate) onTranslate(null);
    } finally {
      setLoading(false);
    }
  };

  const selectedLanguage = languages.find(l => l.code === selectedLang) || languages[0];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              size="sm"
              className="border-emerald-500/50 hover:bg-emerald-500/10 px-3"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  <span className="text-xs">Translating...</span>
                </>
              ) : (
                <>
                  <span className="text-lg mr-2">{selectedLanguage.flag}</span>
                  <span className="text-xs hidden sm:inline">{selectedLanguage.name}</span>
                  <Globe className="w-4 h-4 text-emerald-400 mx-2" />
                  <ChevronDown className="w-3 h-3 text-muted-foreground" />
                </>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="bg-card border-border max-h-[300px] overflow-y-auto">
            <div className="px-2 py-1.5 text-xs text-muted-foreground font-medium sticky top-0 bg-card border-b border-border">
              🌐 AI Translate
            </div>
            {languages.map((lang) => (
              <DropdownMenuItem
                key={lang.code}
                onClick={() => handleTranslate(lang.code)}
                className={`cursor-pointer ${selectedLang === lang.code ? 'bg-emerald-500/20' : ''}`}
              >
                <span className="text-lg mr-2">{lang.flag}</span>
                <span className="flex-1">{lang.name}</span>
                {selectedLang === lang.code && (
                  <Check className="w-4 h-4 ml-2 text-emerald-400" />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {translation && !error && (
          <div className="flex items-center gap-1 text-xs text-emerald-400">
            <Languages className="w-3 h-3" />
            <span className="hidden sm:inline">Translated to {selectedLanguage.name}</span>
            <span className="sm:hidden">{selectedLanguage.flag}</span>
          </div>
        )}
      </div>
      
      {error && (
        <div className="text-xs text-red-400 flex items-center gap-1">
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default LanguageTranslator;
